import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { CurrentUserService } from '@core/account-logic';
import { FeatureDiscoveryActions } from '@core/feature-discovery-data';
import { FeatureDiscoveryFeatures } from '@core/feature-discovery-types';
import { I18nService } from '@core/i18n-legacy';
import { TellentBrand } from '@core/tellent-types';
import { ComponentPositionPair, ComponentTooltipPosition } from '@core/user-interface-types';

import { ProductsPromoService } from './../products-promo.service';
import { PromoTooltip } from './types';
import { ProductsPromoAnalytics } from '../products-promo.analytics';

@Injectable({ providedIn: 'root' })
export class TellentHrIntegrationFeatureDiscovery implements PromoTooltip {
  private readonly productsPromoService = inject(ProductsPromoService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);
  private readonly productsPromoAnalytics = inject(ProductsPromoAnalytics);

  public FEATURE_KEY: keyof FeatureDiscoveryFeatures = 'tellentHrIntegrationPomoUsed';

  public isFeatureDiscovered = toSignal(
    this.currentUserService.featureDiscovered(this.FEATURE_KEY),
  );

  public isEnabled = computed(
    () =>
      !this.isFeatureDiscovered() &&
      this.isTriggered() &&
      this.productsPromoService.hasManageFeaturesPromotion,
  );

  public config = {
    id: 'tellent_hr_integration_feature_discovery',
    isNew: true,
    brand: TellentBrand.Tellent,
    title: this.i18n.translate(
      'app.promote.tellent_hr.tooltip.integration_feature_discovery.title',
    ),
    description: this.i18n.translate(
      'app.promote.tellent_hr.tooltip.integration_feature_discovery.description',
    ),
    ctaText: this.i18n.translate(
      'app.promote.tellent_hr.tooltip.integration_feature_discovery.cta',
    ),
    positions: {
      [ComponentTooltipPosition.RightBottom]: new ComponentPositionPair(
        { originX: 'start', originY: 'center' },
        { overlayX: 'end', overlayY: 'top' },
        'topright',
        -20,
        -30,
      ),
    },
  };

  public isTriggered = signal(false);

  public onDismiss() {
    this.store.dispatch(FeatureDiscoveryActions.update({ payload: { [this.FEATURE_KEY]: true } }));
    this.productsPromoAnalytics.trackKiwiSkipClick('candidateProfile');
  }

  public markAsTriggered = () => {
    this.isTriggered.set(true);
  };

  public onCtaClick(): void {
    this.productsPromoAnalytics.trackKiwiCtaClick('candidateProfile');
  }
}
